/**
 * Presentation helpers for the Suppliers panel (14 §2, 19 §1.3). Contract facts in plain words; nothing here
 * judges a supplier, or the player, as good or bad beyond the service words the server already sends.
 */

const CATEGORY: Record<string, string> = {
  it_msp: "IT support",
  copier_lease: "Copier lease",
  payroll: "Payroll bureau",
  bank: "Bank",
  pms: "Practice management system",
  telephony: "Phones and broadband",
  insurance: "Insurance",
  ai: "AI software",
  reporting_accountant: "Reporting accountant",
  bookkeeping: "Bookkeeping",
  hr: "HR adviser",
  recruitment: "Recruitment agency",
  locum: "Locum agency",
  landlord: "Landlord",
  marketing: "Marketing agency",
};

/** "it_msp" → "IT support"; unknown categories are humanised ("search_provider" → "Search provider"). */
export function categoryLabel(category: string): string {
  const known = CATEGORY[category];
  if (known) return known;
  const words = category.replace(/[_-]+/g, " ").trim();
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : "Not recorded";
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** "2026-03-31" → "31 Mar 2026" (no Date parsing for display, so no time-zone drift). */
export function formatLongDate(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!m) return iso;
  return `${Number(m[3])} ${MONTHS[Number(m[2]) - 1] ?? m[2]} ${m[1]}`;
}

const dayNumber = (iso: string): number | null => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  return m ? Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])) / 86_400_000 : null;
};

/** Notice dates this close to today get a soft warning (in words, not colour alone). */
export const NOTICE_SOON_DAYS = 28;

export interface NoticeInfo {
  /** The date as shown, or null when the contract has no notice date. */
  date: string | null;
  /** A soft note in words: "Soon: in 12 days", "Soon: today", "Date passed". Null when there is nothing to say. */
  note: string | null;
  soon: boolean;
  passed: boolean;
}

/** The notice deadline against the game's current date. */
export function noticeInfo(deadline: string | null, today: string): NoticeInfo {
  if (!deadline) return { date: null, note: null, soon: false, passed: false };
  const d = dayNumber(deadline);
  const t = dayNumber(today);
  const date = formatLongDate(deadline);
  if (d === null || t === null) return { date, note: null, soon: false, passed: false };
  const left = d - t;
  if (left < 0) return { date, note: "Date passed", soon: false, passed: true };
  if (left > NOTICE_SOON_DAYS) return { date, note: null, soon: false, passed: false };
  const when = left === 0 ? "today" : left === 1 ? "tomorrow" : left < 14 ? `in ${left} days` : `in ${Math.round(left / 7)} weeks`;
  return { date, note: `Soon: ${when}`, soon: true, passed: false };
}

/** Pounds a month as the invoices show them: "£4,200", "£1,150.50"; "No fixed fee" for nothing. */
export function formatMonthlyCost(pounds: number): string {
  if (!(pounds > 0)) return "No fixed fee";
  const whole = Math.round(pounds * 100) % 100 === 0;
  return `£${pounds.toLocaleString("en-GB", { minimumFractionDigits: whole ? 0 : 2, maximumFractionDigits: whole ? 0 : 2 })}`;
}
